import { useState } from 'react';
import { Link } from 'react-router-dom';

import Logo from './Logo';
import Eyebrow from './Eyebrow';
import { Ic } from '../utils/icons';
import { authFetch } from './api';

const Soc = ({ d, label, href }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="grid place-items-center w-9 h-9 rounded-lg chip text-slate-600 hover:text-ember hover:border-ember/40 transition-colors shadow-sm">
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d={d} /></svg>
  </a>
);

const inputCls = "w-full bg-slate-50/70 border border-slate-200 rounded-lg px-4 py-3 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition";

function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    try {
      const res = await authFetch('/save_enquiry', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus('ok');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('err');
    }
  };

  if (status === 'ok') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-10 text-center h-full flex flex-col items-center justify-center">
        <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: 'rgba(5, 150, 105, 0.12)' }}>
          {/* eslint-disable-next-line react/jsx-pascal-case */}
          <Ic.check className="w-7 h-7 text-emerald-700" />
        </span>
        <h3 className="mt-5 font-display text-[1.4rem] font-700 text-ink">Message sent.</h3>
        <p className="mt-2 text-[14px] text-mute">We'll get back to you shortly.</p>
        <button onClick={() => setStatus('idle')}
          className="mt-5 text-[13px] font-600 text-mute hover:text-ink transition-colors underline underline-offset-2">
          Send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-md">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Your Name</label>
          <input className={inputCls} value={form.name} onChange={set('name')} required placeholder="John Doe" />
        </div>
        <div>
          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Email Address</label>
          <input className={inputCls} type="email" value={form.email} onChange={set('email')} required placeholder="john@example.com" />
        </div>
      </div>
      <div className="mt-4">
        <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Subject</label>
        <input className={inputCls} value={form.subject} onChange={set('subject')} placeholder="Project inquiry" />
      </div>
      <div className="mt-4">
        <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Message</label>
        <textarea className={inputCls + ' resize-y min-h-[120px]'} value={form.message} onChange={set('message')} required placeholder="Tell us about your project..." rows={5} />
      </div>
      {status === 'err' && (
        <p className="mt-3 text-[12.5px] text-red-500">Couldn't send — please try again or email us directly.</p>
      )}
      <button type="submit" disabled={status === 'sending'}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 text-[14px] font-700 tracking-[0.12em] uppercase text-white px-6 py-3.5 rounded-xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 shadow-md shadow-emerald-600/20"
        style={{ background: 'linear-gradient(100deg, var(--lead), var(--sec))' }}>
        {/* eslint-disable-next-line react/jsx-pascal-case */}
        {status === 'sending' ? 'SENDING...' : <> SEND MESSAGE <Ic.arrow className="w-4 h-4" /> </>}
      </button>
    </form>
  );
}

export default function Footer({ hideForm = false }) {
  return (
    <footer id="contact" className={`relative ${hideForm ? 'pt-10' : 'pt-24'} px-5 sm:px-8 scroll-mt-20`}>
      {/* ── Contact section: CTA left + form right ── */}
      {!hideForm && (
        <div className="max-w-[1240px] mx-auto">
          <div className="reveal relative rounded-2xl border border-slate-200/80 bg-white shadow-xl shadow-slate-900/5 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(70% 120% at 20% 0%, rgba(5,150,105,0.05), transparent 60%), radial-gradient(50% 80% at 80% 100%, rgba(2,132,199,0.04), transparent 60%)' }}></div>
            <div className="relative grid lg:grid-cols-[1fr_1.1fr] gap-0">

              {/* Left — CTA copy */}
              <div className="px-8 sm:px-12 py-14 sm:py-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-slate-200/80">
                <Eyebrow>GET IN TOUCH</Eyebrow>
                <h2 className="mt-5 font-display font-700 text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.02] text-ink">
                  Ready to <span className="text-ember">electrify</span> your infrastructure?
                </h2>
                <p className="mt-5 text-[15px] text-mute leading-relaxed max-w-[44ch]">
                  Tell us where your charging network hits its limits. We'll show you what's possible.
                </p>

                <div className="mt-8 flex flex-col gap-3">
                  <a href="tel:+919266031789" className="group inline-flex items-center gap-3 text-[14px] text-mute hover:text-ink transition-colors">
                    <span className="grid place-items-center w-9 h-9 rounded-lg chip text-ember group-hover:border-ember/40 transition-colors shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><path d="M5 4h3l1.5 5-2 1.5a12 12 0 0 0 6 6l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                    </span>
                    +91 9266031789
                  </a>
                  <a href="mailto:info@emberonlabs.in" className="group inline-flex items-center gap-3 text-[14px] text-mute hover:text-ink transition-colors">
                    <span className="grid place-items-center w-9 h-9 rounded-lg chip text-cyan group-hover:border-cyan/40 transition-colors shrink-0">
                      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                    </span>
                    info@emberonlabs.in
                  </a>
                </div>

                <div className="mt-10">
                  <a href="https://chargify.co.in" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[14px] font-600 tracking-[0.12em] uppercase text-slate-800 chip rounded-xl px-6 py-3.5 transition-transform hover:scale-[1.03] active:scale-95 shadow-sm">
                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                    SEE CHARGIFY <Ic.arrow className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>

              {/* Right — contact form */}
              <div className="px-8 sm:px-12 py-14 sm:py-16">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Footer bar ── */}
      <div className="max-w-[1240px] mx-auto mt-2 border-t border-slate-200 py-10">
        <div className="flex flex-col md:flex-row gap-8 justify-between">
          <div className="max-w-[300px]">
            <Logo />
            <p className="mt-4 text-[13px] text-slate-500 leading-relaxed">
              R&amp;D powerhouse for e-mobility &amp; EV infrastructure. Software ecosystems and hardware, engineered in-house.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-10">
            {[
              ['Products', [['Chargify CMS', 'https://chargify.co.in'], ['PMMS', 'https://pmms.emberonlabs.in'], ['MMMS', 'https://mmms.emberonlabs.in']]],
              ['Navigate', [['Contact', '/#contact-section'], ['Blogs', '/blogs'], ['Support', '/support'], ['Privacy Policy', '/privacy-policy'], ['Admin', '/admin']]],
              ['Contact Us', [['info@emberonlabs.in', 'https://mail.google.com/mail/?view=cm&fs=1&to=info@emberonlabs.in'], ['+91 9266031789', 'tel:+919266031789']]],
            ].map(([title, rows]) => (
              <div key={title}>
                <div className="text-[11px] font-700 tracking-[0.16em] text-slate-700">{String(title).toUpperCase()}</div>
                <ul className="mt-4 space-y-2.5">
                  {rows.map(([l, h]) => (
                    <li key={l}>
                      {h.includes('#') ? (
                        <a href={h} onClick={(e) => {
                          const id = h.split('#')[1];
                          const el = document.getElementById(id);
                          if (el) {
                            e.preventDefault();
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } else if (id === 'top' && window.location.pathname === '/') {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }} className="text-[13px] text-mute hover:text-ember transition-colors cursor-pointer">{l}</a>
                      ) : h.startsWith('http') || h.startsWith('tel:') || h.startsWith('mailto:') ? (
                        <a href={h} target={h.startsWith('http') ? "_blank" : undefined} rel={h.startsWith('http') ? "noopener noreferrer" : undefined} className="text-[13px] text-mute hover:text-ember transition-colors">{l}</a>
                      ) : (
                        <Link to={h} className="text-[13px] text-mute hover:text-ember transition-colors">{l}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[12px] text-slate-500">© {new Date().getFullYear()} Emberon Labs. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/delete-account" className="text-[12px] font-600 text-slate-600 hover:text-red-600 transition-colors border border-slate-200 hover:border-red-300 rounded-lg px-3 py-1.5 shadow-sm">
              Delete Account
            </Link>
            <div className="flex items-center gap-2.5">
              <Soc label="Emberon Labs on LinkedIn" href="https://www.linkedin.com/company/emberon-labs/" d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5a2.5 2.5 0 0 0-.02-5ZM3 9h4v12H3V9Zm6 0h3.8v1.64h.05c.53-1 1.83-2.06 3.76-2.06 4.02 0 4.76 2.64 4.76 6.08V21h-4v-5.34c0-1.27-.02-2.9-1.77-2.9-1.77 0-2.04 1.38-2.04 2.81V21H9V9Z" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
